// JadEye AI API Library
// AI 분석 전용 API 함수들

import { api, type ApiOptions } from './api';

// AI 분석 요청/응답 타입 정의
export interface HealthAnalysisRequest {
    analysis_type?: 'quick' | 'comprehensive' | 'specialized';
    period?: 'week' | 'month' | '3months';
    include_data?: {
        fitness?: boolean;
        health_metrics?: boolean;
        symptoms?: boolean;
        medications?: boolean;
    };
    focus_areas?: string[];
}

export interface WorkoutRecommendationRequest {
    fitness_level: 'beginner' | 'intermediate' | 'advanced';
    available_time: number;
    preferred_activities?: string[];
    health_conditions?: string[];
    goals?: string[];
    equipment?: string[];
}

export interface NutritionAnalysisRequest {
    analysis_period?: 'day' | 'week' | 'month';
    dietary_preferences?: string[];
    allergies?: string[];
    health_goals?: string[];
    activity_level?: 'low' | 'moderate' | 'high';
}

export interface InsightsQuery {
    period?: 'day' | 'week' | 'month' | 'all';
    category?: 'activity' | 'diet' | 'health' | 'medication' | 'symptom' | 'all';
    severity?: 'info' | 'warning' | 'alert' | 'critical' | 'all';
    limit?: number;
}

export interface CreditUsageQuery {
    period?: 'week' | 'month' | '3months' | 'year';
}

// AI 분석 결과 타입들
export interface HealthAnalysisResult {
    analysis_id: string;
    user_id: number;
    health_score: number;
    analysis_summary: string;
    categories: {
        cardio_fitness: CategoryScore;
        weight_management: CategoryScore & { trend: string };
        activity_patterns: CategoryScore;
    };
    risk_factors: RiskFactor[];
    achievements: string[];
    next_goals: Goal[];
    credits_used: number;
    generated_at: string;
    expires_at: string;
}

export interface CategoryScore {
    score: number;
    status: 'excellent' | 'good' | 'average' | 'poor';
    insights: string[];
    recommendations: string[];
}

export interface RiskFactor {
    category: string;
    level: 'high' | 'medium' | 'low';
    description: string;
    prevention_tips: string[];
}

export interface Goal {
    category: string;
    goal: string;
    timeline: string;
    difficulty: 'easy' | 'medium' | 'hard';
}

export interface WorkoutPlan {
    recommendation_id: string;
    personalized_plan: {
        title: string;
        duration_weeks: number;
        sessions_per_week: number;
        weekly_schedule: WorkoutSession[];
    };
    adaptation_tips: string[];
    safety_guidelines: string[];
    credits_used: number;
    generated_at: string;
}

export interface WorkoutSession {
    day: string;
    workout: {
        title: string;
        duration: number;
        exercises: Exercise[];
        estimated_calories: number;
    };
}

export interface Exercise {
    name: string;
    sets?: number;
    reps?: string;
    duration?: string;
    rest?: string;
    difficulty: string;
    description: string;
}

export interface NutritionAnalysis {
    nutrition_score: number;
    calorie_analysis: {
        avg_daily_intake: number;
        recommended_intake: number;
        status: string;
        weekly_trend: string;
    };
    macro_balance: {
        carbs: MacroInfo;
        protein: MacroInfo;
        fat: MacroInfo;
    };
    meal_recommendations: MealRecommendation[];
    nutrition_tips: string[];
    deficiency_alerts: DeficiencyAlert[];
    credits_used: number;
    generated_at: string;
}

export interface MacroInfo {
    percentage: number;
    recommended: string;
    status: string;
}

export interface MealRecommendation {
    meal_type: string;
    suggested_foods: string[];
    calories: string;
    prep_time: string;
}

export interface DeficiencyAlert {
    nutrient: string;
    level: string;
    recommendation: string;
}

export interface AiInsight {
    id: number;
    insight_type: string;
    category: 'activity' | 'diet' | 'health' | 'medication' | 'symptom' | 'general';
    severity: 'info' | 'warning' | 'alert' | 'critical';
    priority: number;
    /** 다국어 원본 (예: { ko: '...', en: '...' }) — 표시에는 *_localized 사용 권장 */
    title: Record<string, string>;
    content: Record<string, string>;
    /** 서버(HasTranslatableFields)가 현재 locale 로 골라준 표시용 값 */
    title_localized: string;
    content_localized: string;
    recommendations: string[] | null;
    data_sources?: Record<string, any> | null;
    confidence_score: string | null;
    is_read: boolean;
    is_helpful?: boolean | null;
    expires_at?: string | null;
    created_at: string;
}

export interface InsightsResponse {
    insights: AiInsight[];
    summary: {
        total_insights: number;
        critical_count: number;
        alert_count: number;
        unread_count: number;
    };
}

export interface CreditUsageResponse {
    period: string;
    total_used: number;
    remaining_balance: number;
    usage_breakdown: FeatureUsage[];
    monthly_trend: {
        this_period: number;
        last_period: number;
        change_percentage: number;
    };
}

export interface FeatureUsage {
    feature: string;
    feature_name: string;
    count: number;
    credits_per_use: number;
    total_credits: number;
    last_used: string;
}

/**
 * JadEye AI API 클래스
 */
class JadEyeAiAPI {
    /**
     * AI 건강 분석 요청
     */
    async requestHealthAnalysis(
        request: HealthAnalysisRequest,
        options: Omit<ApiOptions, 'method' | 'data'> = {}
    ) {
        return api.post<HealthAnalysisResult>('/api/v3/ai/health-analysis', request, {
            timeout: 30000, // 30초 타임아웃
            ...options
        });
    }

    /**
     * AI 운동 계획 추천
     */
    async getWorkoutRecommendations(
        request: WorkoutRecommendationRequest,
        options: Omit<ApiOptions, 'method' | 'data'> = {}
    ) {
        return api.post<WorkoutPlan>('/api/v3/ai/workout-recommendations', request, {
            timeout: 30000,
            ...options
        });
    }

    /**
     * AI 영양 분석
     */
    async analyzeNutrition(
        request: NutritionAnalysisRequest,
        options: Omit<ApiOptions, 'method' | 'data'> = {}
    ) {
        return api.post<NutritionAnalysis>('/api/v3/ai/nutrition-analysis', request, {
            timeout: 30000,
            ...options
        });
    }

    /**
     * AI 인사이트 조회
     */
    async getInsights(
        query: InsightsQuery = {},
        options: Omit<ApiOptions, 'method'> = {}
    ) {
        const params = new URLSearchParams();
        Object.entries(query).forEach(([key, value]) => {
            if (value !== undefined) {
                params.append(key, String(value));
            }
        });

        const url = `/api/v3/ai/insights${params.toString() ? `?${params.toString()}` : ''}`;
        return api.get<InsightsResponse>(url, options);
    }

    /**
     * AI 크레딧 사용 내역 조회
     */
    async getCreditUsage(
        query: CreditUsageQuery = {},
        options: Omit<ApiOptions, 'method'> = {}
    ) {
        const params = new URLSearchParams();
        Object.entries(query).forEach(([key, value]) => {
            if (value !== undefined) {
                params.append(key, String(value));
            }
        });

        const url = `/api/v3/ai/credit-usage${params.toString() ? `?${params.toString()}` : ''}`;
        return api.get<CreditUsageResponse>(url, options);
    }

    /**
     * 크레딧 잔액 조회 (기존 API 활용)
     */
    async getCreditBalance(options: Omit<ApiOptions, 'method'> = {}) {
        return api.get('/api/v3/payment/credits', options);
    }

    /**
     * 기능 접근 권한 확인 (기존 API 활용)
     */
    async checkFeatureAccess(featureCode: string, options: Omit<ApiOptions, 'method'> = {}) {
        return api.get(`/api/v3/payment/credits/check-access?feature=${featureCode}`, options);
    }
}

// 싱글톤 인스턴스
export const aiApi = new JadEyeAiAPI();

// 기본 export
export default aiApi;